import { useEffect, useState } from 'react';

import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@cast/design-system';
import { CAST_AI } from '@cast/constants';
import { ClusterOnboardingPathEnum } from '@cast/types';

import { AgentCodeSnippet } from 'components/code';
import { InlineCode } from 'components/InlineCode';
import { ScriptSection } from 'hooks/dialogs/cluster/_components/new-onboarding/ScriptSection';
import { TerraformTabContent } from 'hooks/dialogs/cluster/_components/new-onboarding/TerraformTabContent';
import { HelmScriptWarning } from 'hooks/dialogs/cluster/_components/new-onboarding/HelmScriptWarning';
import { useConnectAndEnableCastAiScriptQuery } from 'hooks/queries/cluster/useConnectAndEnableCastAiScriptQuery';

import {
  CASTCTL_PLACEHOLDER_CLUSTER_PROVIDER,
  type CastctlFeatureSelection,
} from 'hooks/dialogs/cluster/components/CastctlOnboardingDialog';
import {
  InstallCastctlCommand,
  InstallCastctlOperatingSystemToggle,
  InstallStepRow,
  type OperatingSystem,
} from 'hooks/dialogs/cluster/components/CastctlOnboardingDialog/InstallCastctlSteps';

import { getCastctlCommandFlags } from './getCastctlCommandFlags';

const HELM_DISCLAIMER = `This script installs ${CAST_AI}'s read-only agent and spot interruption handler. Remember to change your cloud provider if you're onboarding an AKS or GKE cluster - the script below sets castai.provider to EKS by default.`;

type ConnectMethod = 'castctl' | 'helm' | 'terraform';

type ScriptState = {
  isLoading: boolean;
  hasError: boolean;
};

type CastctlConnectMethodTabsProps = {
  /**
   * Feature selection driving the castctl connect command. When `undefined`,
   * every `install*` flag is omitted so castctl picks the feature set itself.
   * The Helm request always forces `installAutoscalerAgent=false` and is
   * skipped when the autoscaler is selected (Helm onboarding does not
   * support it).
   */
  featureSelection?: CastctlFeatureSelection;
  /** Optional callback invoked when the script loading/error state changes. */
  onScriptStateChange?: (state: ScriptState) => void;
  /** Optional testId override for the tabs container. */
  testId?: string;
  /**
   * Optional testId for the castctl connect command snippet.
   * Defaults to `'castctl-connect-script'`.
   */
  castctlScriptTestId?: string;
};

// When `featureSelection` is `undefined`, every install flag is omitted so
// castctl picks the feature set itself. The Helm request forces
// `installAutoscalerAgent=false` regardless, so both paths share this object.
const ALL_INSTALL_FLAGS_UNDEFINED = {
  installGPUPlugin: undefined,
  installSecurityAgent: undefined,
  installAutoscalerAgent: undefined,
  installGpuMetricsExporter: undefined,
  installReliabilityMetrics: undefined,
  installNetflowExporter: undefined,
  installAiOptimizerProxy: undefined,
  installWorkloadAutoscaler: undefined,
  installOmni: undefined,
  installPodMutator: undefined,
  installSecurityImageScanning: undefined,
  installSecurityCompliance: undefined,
} as const;

// Non-toggleable feature flags, always off. Combined with the user-selected
// flags when `featureSelection` is provided.
const NON_AUTOMATION_FLAGS_OFF = {
  installGPUPlugin: false,
  installSecurityAgent: false,
  installSecurityCompliance: false,
  installSecurityImageScanning: false,
  installNetflowExporter: false,
  installGpuMetricsExporter: false,
  installOmni: false,
  installAiOptimizerProxy: false,
  installPodMutator: false,
} as const;

export const CastctlConnectMethodTabs = ({
  featureSelection,
  onScriptStateChange,
  testId = 'castctl-connect-method-tabs',
  castctlScriptTestId = 'castctl-connect-script',
}: CastctlConnectMethodTabsProps) => {
  const [method, setMethod] = useState<ConnectMethod>('castctl');
  const [operatingSystem, setOperatingSystem] =
    useState<OperatingSystem>('macos');

  const installFlagParams = featureSelection
    ? {
        ...getCastctlCommandFlags(featureSelection),
        ...NON_AUTOMATION_FLAGS_OFF,
      }
    : ALL_INSTALL_FLAGS_UNDEFINED;

  // Helm onboarding does not support the autoscaler — when it is selected we
  // skip the Helm request and surface the warning instead.
  const isHelmUnsupported = !!featureSelection?.autoscaler;
  const showHelmWarning = method === 'helm' && isHelmUnsupported;

  const castctlQuery = useConnectAndEnableCastAiScriptQuery({
    clusterProvider: CASTCTL_PLACEHOLDER_CLUSTER_PROVIDER,
    ...installFlagParams,
    onboardingPath: ClusterOnboardingPathEnum.CASTCTL,
    enabled: method === 'castctl',
  });

  const helmQuery = useConnectAndEnableCastAiScriptQuery({
    clusterProvider: CASTCTL_PLACEHOLDER_CLUSTER_PROVIDER,
    ...installFlagParams,
    // Force off — Helm does not support the autoscaler, so the backend must
    // never receive a true/default-true value on this path.
    installAutoscalerAgent: false,
    onboardingPath: ClusterOnboardingPathEnum.HELM,
    enabled: method === 'helm' && !isHelmUnsupported,
  });

  const activeQuery = method === 'castctl' ? castctlQuery : helmQuery;

  // When Helm is skipped (autoscaler selected), react-query reports no
  // loading/error — guard explicitly so the reported state stays clean.
  const isHelmSkipped = method === 'helm' && isHelmUnsupported;
  const isLoading = isHelmSkipped
    ? false
    : activeQuery.isLoading || activeQuery.isFetching;
  const hasError = isHelmSkipped ? false : !!activeQuery.error;

  useEffect(() => {
    onScriptStateChange?.({ isLoading, hasError });
  }, [isLoading, hasError, onScriptStateChange]);

  return (
    <Stack gap={16} data-testid={testId}>
      <Stack direction="row" alignItems="center" gap={8}>
        <Typography variant="A1">Connect with:</Typography>
        <ToggleButtonGroup
          size="sm"
          value={method}
          exclusive
          onChange={(_event, value: ConnectMethod) => {
            if (value) {
              setMethod(value);
            }
          }}
          testId="connect-method-toggle"
        >
          <ToggleButton
            value="castctl"
            text="castctl"
            testId="connect-method-castctl"
          />
          <ToggleButton value="helm" text="Helm" testId="connect-method-helm" />
          <ToggleButton
            value="terraform"
            text="Terraform"
            testId="connect-method-terraform"
          />
        </ToggleButtonGroup>
      </Stack>

      <Box>
        {method === 'castctl' && (
          <Stack>
            <InstallStepRow
              number={1}
              title="Install castctl"
              description="Skip this step if castctl is already installed."
            >
              <Stack gap={8}>
                <InstallCastctlOperatingSystemToggle
                  value={operatingSystem}
                  onChange={setOperatingSystem}
                />
                <InstallCastctlCommand operatingSystem={operatingSystem} />
              </Stack>
            </InstallStepRow>

            <InstallStepRow
              number={2}
              isLast
              title="Connect your cluster"
              description={
                <>
                  Run from a terminal where <InlineCode>kubectl</InlineCode>{' '}
                  context is set to the cluster you want to connect.
                </>
              }
            >
              <AgentCodeSnippet
                code={castctlQuery.script}
                isLoading={castctlQuery.isLoading || castctlQuery.isFetching}
                error={castctlQuery.error}
                refetch={castctlQuery.refetch}
                testId={castctlScriptTestId}
              />
            </InstallStepRow>
          </Stack>
        )}

        {method === 'helm' && (
          <ScriptSection
            script={helmQuery.script}
            isLoading={helmQuery.isLoading || helmQuery.isFetching}
            refetch={helmQuery.refetch}
            error={helmQuery.error}
            disclaimer={HELM_DISCLAIMER}
            customScriptContent={
              showHelmWarning ? <HelmScriptWarning /> : undefined
            }
          />
        )}

        {method === 'terraform' && <TerraformTabContent />}
      </Box>
    </Stack>
  );
};
